import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ loading: true, status: null, payment_status: null, granted: false, error: null });
  const [attempts, setAttempts] = useState(0);

  const sessionId = params.get("session_id");

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ loading: false, error: "No session_id", status: null, payment_status: null, granted: false });
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setState({
        loading: false,
        status: data.status,
        payment_status: data.payment_status,
        granted: !!data.granted,
        sku: data.sku,
        amount: data.amount_usd,
        error: null,
      });
      if (data.payment_status === "paid" || data.status === "expired") {
        await refresh();
        return true;
      }
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || "Error" }));
      return true;
    }
    return false;
  }, [sessionId, refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const done = await poll();
      if (done || cancelled) return;
      const t = setTimeout(() => !cancelled && setAttempts((a) => a + 1), POLL_MS);
      return () => clearTimeout(t);
    })();
    return () => { cancelled = true; };
  }, [poll, attempts]);

  const paid = state.payment_status === "paid";
  const failed = !!state.error;
  const expired = state.status === "expired" || attempts >= MAX_ATTEMPTS;
  const terminal = paid || expired || failed;

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="max-w-lg w-full p-10 rounded-3xl bg-white border border-[#E6E2D8] text-center">
        {paid ? (
          <CheckCircle className="mx-auto text-[#5F7161] mb-4" size={56} strokeWidth={1.5} />
        ) : terminal ? (
          <XCircle className="mx-auto text-[#D96C6C] mb-4" size={56} strokeWidth={1.5} />
        ) : (
          <Loader2 className="mx-auto text-[#5F7161] mb-4 animate-spin" size={56} strokeWidth={1.5} />
        )}
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-3xl font-light mb-2">
          {paid ? "Payment complete" : failed ? "Payment error" : expired ? "Couldn't confirm payment" : "Confirming payment…"}
        </h1>
        <p className="text-[#2C332A]/70 mb-6">
          {paid
            ? `Your purchase (${state.sku}) has been applied. Enjoy!`
            : failed
            ? state.error
            : expired
            ? "We couldn't verify the payment. Check your email or contact support."
            : `Checking Stripe… attempt ${attempts + 1} / ${MAX_ATTEMPTS}`}
        </p>
        {terminal && (
          <div className="flex gap-3 justify-center">
            <Button
              data-testid="payments-status-dashboard"
              onClick={() => navigate("/dashboard")}
              className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
            >
              Back to dashboard
            </Button>
            <Button
              data-testid="payments-status-store"
              variant="outline"
              onClick={() => navigate("/store")}
              className="rounded-full border-[#E6E2D8]"
            >
              Back to store
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
